import { ChangeEvent, useCallback, useMemo } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { TIME_FILTERS } from "../../Components/Filters/Hooks/useTimeFilterHook";
import {
  BaseGroupCandidateFilters,
  BaseGroupPagination,
  GroupFilterValues,
  PaginationValues,
} from "../../Typings/Pagination";
import { ITimeFilter } from "../../Typings/SearchFilters";
import { createURI } from "../../Utilities/createURI";
import { STORAGE_VERSION_DATE } from "../../Utilities/browserStorage";

export const defaultGroupCandidatesPagination = {
  page: 0,
  pageSize: 10,
  sortBy: "createdDate",
  sortDirection: "desc" as "asc" | "desc",
} as PaginationValues;

export const defaultGroupCandidatesFilter = {
  tags: [],
  timeFilter: TIME_FILTERS["all-time-button"],
} as GroupFilterValues;

export const groupCandidatesFilterStore = create<BaseGroupCandidateFilters>()(
  persist(
    (set, get) =>
      ({
        setTags: (groupId, tags) => {
          const groupState = get()[groupId] ?? defaultGroupCandidatesFilter;
          set((state) => ({ ...state, [groupId]: { ...groupState, tags } }));
        },
        setTimeFilter: (groupId, timeFilter) => {
          const groupState = get()[groupId] ?? defaultGroupCandidatesFilter;
          set((state) => ({ ...state, [groupId]: { ...groupState, timeFilter } }));
        },
      }) as BaseGroupCandidateFilters,
    {
      name: `group-candidates-filter-${STORAGE_VERSION_DATE}`,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(([key]) => !key.startsWith("set") && !key.startsWith("handle")),
        ),
    },
  ),
);

export const groupCandidatesPaginationStore = create<BaseGroupPagination>()(
  persist(
    (set, get) =>
      ({
        setPage: (groupId, page) => {
          const groupState = get()[groupId] ?? defaultGroupCandidatesPagination;
          set((state) => ({ ...state, [groupId]: { ...groupState, page } }));
        },
        setPageSize: (groupId, pageSize) => {
          const groupState = get()[groupId] ?? defaultGroupCandidatesPagination;
          set((state) => ({ ...state, [groupId]: { ...groupState, pageSize, page: 0 } }));
        },
        setSortBy: (groupId, sortBy) => {
          set((state) => {
            const groupState = get()[groupId] || defaultGroupCandidatesPagination;
            const isAsc = sortBy === groupState.sortBy && groupState.sortDirection === "asc";
            return {
              ...state,
              [groupId]: {
                ...groupState,
                sortBy,
                sortDirection: isAsc ? "desc" : "asc",
                page: 0,
              },
            };
          });
        },
        setSortDirection: (groupId, sortDirection) => {
          const groupState = get()[groupId] ?? defaultGroupCandidatesPagination;
          set((state) => ({ ...state, [groupId]: { ...groupState, sortDirection } }));
        },
      }) as BaseGroupPagination,
    {
      name: `group-candidates-pagination-${STORAGE_VERSION_DATE}`,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(([key]) => !key.startsWith("set") && !key.startsWith("handle")),
        ),
    },
  ),
);

/**
 * Re-useable hook for managing pagination state for group profiles, only includes pagination state
 *
 * Allows multiple groups to use the same pagination store without conflicting with each other
 * @param groupId - The key to use in the saved session storage object *must be unique per group*
 * @returns the current state of the candidates filter store and the handlers to update the store
 */
export const useGroupCandidatesPaginationStore = (groupId: string) => {
  const { page, pageSize, sortBy, sortDirection, setPage, setPageSize, setSortBy } =
    groupCandidatesPaginationStore((state) => {
      const groupState = state[groupId] ?? defaultGroupCandidatesPagination;
      return {
        page: groupState.page,
        pageSize: groupState.pageSize,
        sortBy: groupState.sortBy,
        sortDirection: groupState.sortDirection,
        setPage: state.setPage,
        setPageSize: state.setPageSize,
        setSortBy: state.setSortBy,
      };
    });

  const handleChangePage = (_: unknown, newPage: number) => setPage(groupId, newPage);
  const handleChangePageSize = (event: ChangeEvent<HTMLInputElement>) =>
    setPageSize(groupId, parseInt(event.target.value, 10));
  const handleRequestSort = (property: string) => setSortBy(groupId, property);

  return {
    page,
    pageSize,
    sortBy,
    sortDirection,
    setPage,
    handleChangePage,
    handleChangePageSize,
    handleRequestSort,
  } as const;
};

/**
 * Re-useable hook for managing pagination state for group profiles, includes full filter state
 *
 * Allows multiple groups to use the same pagination store without conflicting with each other
 * @param groupId - The key to use in the saved session storage object *must be unique per group*
 * @returns the current state of the candidates filter store and the handlers to update the store
 */
export const useGroupCandidatesFilterStore = (groupId: string) => {
  const { tags, timeFilter, setTags, setTimeFilter } = groupCandidatesFilterStore((state) => {
    const groupState = state[groupId] ?? defaultGroupCandidatesFilter;
    return {
      tags: groupState.tags,
      timeFilter: groupState.timeFilter,
      setTags: state.setTags,
      setTimeFilter: state.setTimeFilter,
    };
  });
  const { page, pageSize, sortBy, sortDirection, setPage } = useGroupCandidatesPaginationStore(groupId);

  const getFilterQueryString = useCallback(
    (isPaginated = true, nextPage = false) => {
      const params = [] as string[][];

      if (isPaginated) {
        const newPage = nextPage ? page + 2 : page + 1;
        params.push(["page", newPage.toString()]);
        params.push(["pageSize", pageSize.toString()]);
        params.push(["sort", sortDirection]);
        params.push(["sortBy", sortBy]);
      }

      if (timeFilter?.value) {
        params.push(["daysSubmitted", timeFilter.value.toString()]);
      }

      tags.forEach((tag) => params.push(["tags", createURI(tag)]));

      return params.length > 0 ? `?${params.map((param) => param.join("=")).join("&")}` : "";
    },
    [tags, timeFilter?.value, page, pageSize, sortDirection, sortBy],
  );

  const filterQueryString = useMemo(() => getFilterQueryString(), [getFilterQueryString]);
  const idsFilterQueryString = useMemo(() => getFilterQueryString(false), [getFilterQueryString]);
  const page2FilterQueryString = useMemo(() => getFilterQueryString(true, true), [getFilterQueryString]);

  const handleTagsChange = (tags: string[]) => {
    setTags(groupId, tags);
    setPage(groupId, 0);
  };

  const handleTimeFilterChange = (timeFilter: ITimeFilter) => {
    setTimeFilter(groupId, timeFilter);
    setPage(groupId, 0);
  };

  const handleClearFilters = () => {
    setTags(groupId, []);
    setTimeFilter(groupId, TIME_FILTERS["all-time-button"]);
    setPage(groupId, 0);
  };

  return {
    page,
    pageSize,
    sortBy,
    sortDirection,
    tags,
    timeFilter,
    filterQueryString,
    idsFilterQueryString,
    page2FilterQueryString,
    handleClearFilters,
    handleTagsChange,
    handleTimeFilterChange,
  } as const;
};

export type GroupCandidatesFilterStore = ReturnType<typeof useGroupCandidatesFilterStore>;
export type GroupCandidatesPaginationStore = ReturnType<typeof useGroupCandidatesPaginationStore>;
